import CardWithTitleAndButton from 'components/CardWithTitleAndButton';
import ButtonV2 from 'components/common/ButtonV2';
import ReactSelect from 'components/Fields/ReactSelect';
import { authContext } from 'contexts/AuthContext';
import { Field } from 'formik';
import React, { useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { axiosGetAPI, axiosPost, axiosUpdate } from 'services';
import AddressForm from './AddressForm';

const plusIconSvg = (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.00146 1.24976C5.08435 1.24976 5.16454 1.28295 5.22314 1.34155C5.28163 1.40004 5.31384 1.47955 5.31396 1.56226V4.68726H8.43896C8.52184 4.68726 8.60204 4.72045 8.66064 4.77905C8.71913 4.83754 8.75134 4.91705 8.75146 4.99976C8.75146 5.08264 8.71925 5.16283 8.66064 5.22144C8.60204 5.28004 8.52184 5.31226 8.43896 5.31226H5.31396V8.43726C5.31396 8.52014 5.28175 8.60033 5.22314 8.65894C5.16454 8.71754 5.08435 8.74976 5.00146 8.74976C4.91876 8.74963 4.83925 8.71742 4.78076 8.65894C4.72216 8.60033 4.68896 8.52014 4.68896 8.43726V5.31226H1.56396C1.48126 5.31213 1.40175 5.27992 1.34326 5.22144C1.28466 5.16283 1.25146 5.08264 1.25146 4.99976C1.25159 4.91705 1.28477 4.83755 1.34326 4.77905C1.40175 4.72056 1.48126 4.68738 1.56396 4.68726H4.68896V1.56226C4.68909 1.47955 4.72227 1.40005 4.78076 1.34155C4.83925 1.28306 4.91876 1.24988 5.00146 1.24976Z"
      fill="white"
    />
  </svg>
);

const AddressSection = ({ values, setFieldValue }) => {
  const [showAddressForm, setShowAddressForm] = React.useState(0);

  const [addressList, setAddressList] = React.useState([]);
  const [sellerAddressList, setSellerAddressList] = React.useState([]);

  const { auth } = useContext(authContext);

  const buyerId =
    values?.buyer?.id ??
    (typeof values?.buyer === 'number' || typeof values?.buyer === 'string'
      ? values?.buyer
      : null);

  const sellerId =
    values?.seller?.id ??
    (typeof values?.seller === 'number' || typeof values?.seller === 'string'
      ? values?.seller
      : null);

  const fetchAddresses = async (userId, updateAddressList) => {
    if (!userId) return;
    try {
      const resp = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}address/getall-address-for-a-user/${userId}`,
        auth,
      );
      if (resp?.isSuccessful) {
        updateAddressList(resp?.data?.data || []);
      }
    } catch (error) {
      console.error('Error fetching Addresses:', error);
    }
  };

  const getAddressText = (field) => {
    if (!field) return '';

    if (typeof field === 'string') {
      return field.trim();
    }

    if (typeof field !== 'object') {
      return '';
    }

    return (
      field?.cityName ||
      field?.stateName ||
      field?.country ||
      field?.countryName ||
      field?.name ||
      field?.label ||
      ''
    );
  };

  const isAddressInvalidOrNull = (text) => {
    if (!text || typeof text !== 'string') return true;
    const cleaned = text
      .replace(/\bnull\b/gi, '')
      .replace(/\bundefined\b/gi, '')
      .replace(/^[\s,]+|[\s,]+$/g, '')
      .trim();
    return !cleaned;
  };

  const formatAddress = (addressObj) => {
    if (!addressObj) return '';

    if (typeof addressObj === 'string') {
      const trimmed = addressObj.trim();
      return isAddressInvalidOrNull(trimmed) ? '' : trimmed;
    }

    const rawText =
      addressObj?.displayAddress ||
      addressObj?.addressText ||
      addressObj?.orderDeliveryAddress ||
      addressObj?.buyerGstBillingAddress ||
      addressObj?.pickupAddressText;

    if (rawText && !isAddressInvalidOrNull(String(rawText))) {
      return String(rawText).trim();
    }

    const addressParts = [
      addressObj?.houseNumber,
      addressObj?.street,
      addressObj?.area,
      getAddressText(addressObj?.city),
      getAddressText(addressObj?.state),
      getAddressText(addressObj?.country),
      addressObj?.pinCode,
    ].filter(
      (part) =>
        part &&
        String(part).trim() !== '' &&
        String(part).trim() !== '{}' &&
        String(part).trim().toLowerCase() !== 'null' &&
        String(part).trim().toLowerCase() !== 'undefined',
    );

    return addressParts.join(', ');
  };

  const normalizeCurrentAddress = (addressObj, fallbackText) => {
    if (!addressObj && !fallbackText) {
      return null;
    }

    if (typeof addressObj === 'string') {
      const trimmedText = addressObj.trim();
      if (!trimmedText || isAddressInvalidOrNull(trimmedText)) {
        return null;
      }

      return {
        id: 0,
        street: trimmedText,
        displayAddress: trimmedText,
      };
    }

    const trimmedText = String(fallbackText || '').trim();
    const isFallbackValid = !isAddressInvalidOrNull(trimmedText);

    const normalized = addressObj ? { ...addressObj } : {};

    if (isFallbackValid) {
      normalized.displayAddress = trimmedText;
      if (!normalized.street) {
        normalized.street = trimmedText;
      }
    }

    const formatted = formatAddress(normalized);
    if (!formatted) {
      return null;
    }

    if (!normalized.id) {
      normalized.id = 0;
    }

    return normalized;
  };

  const ensureCurrentAddressInList = (list, currentAddress) => {
    const safeList = Array.isArray(list) ? list : [];

    if (!currentAddress) {
      return safeList;
    }

    const currentId = currentAddress?.id;
    const alreadyIncluded =
      currentId !== undefined && currentId !== null
        ? safeList.some((item) => String(item?.id) === String(currentId))
        : false;

    if (alreadyIncluded) {
      return safeList;
    }

    const currentLabel = formatAddress(currentAddress);
    if (!currentLabel) {
      return safeList;
    }

    return [currentAddress, ...safeList];
  };

  useEffect(() => {
    if (buyerId) {
      fetchAddresses(buyerId, setAddressList);
    }
    if (sellerId) {
      fetchAddresses(sellerId, setSellerAddressList);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId, sellerId, auth]);

  const setAddressToOrder = (payload) => {
    try {
      axiosUpdate(
        `${process.env.REACT_APP_REST_API_ENDPOINT}address/set-address-to-order/${values?.id}`,
        payload,
        auth?.data?.token,
      );
    } catch (error) {
      console.error(error);
    }
  };

  const addNewAddress = async (userType) => {
    const order = JSON.parse(JSON.stringify(values));
    const addressInfo = order?.addressInfo;
    const targetUserId = userType === 1 ? buyerId : sellerId;
    try {
      if (addressInfo && targetUserId) {
        const resp = await axiosPost(
          `${process.env.REACT_APP_REST_API_ENDPOINT}address/create-address/${targetUserId}`,
          {
            ...addressInfo,
            state: addressInfo?.state?.id,
            city: addressInfo?.city?.id,
            country: addressInfo?.country?.id,
            pinCode: addressInfo?.pinCode ?? '',
            area: addressInfo?.area ?? '',
          },
          {},
          auth,
        );

        if (resp?.isSuccessful) {
          toast.success('Address updated successfully!');
        }

        if (userType === 1) {
          fetchAddresses(buyerId, setAddressList);
        } else {
          fetchAddresses(sellerId, setSellerAddressList);
        }
      }
    } catch (error) {
      console.error('Error while creating address:', error);
    }
  };

  function getDeliveryValue(formdata) {
    return normalizeCurrentAddress(
      formdata?.deliveryAddress,
      formdata?.orderDeliveryAddress,
    );
  }

  function getBillingValue(formData) {
    return normalizeCurrentAddress(
      formData?.billingAddress,
      formData?.buyerGstBillingAddress,
    );
  }

  function getPickupValue(formData) {
    return normalizeCurrentAddress(
      formData?.pickupAddress,
      formData?.pickupAddressText,
    );
  }

  function getDeliveryAddressList(formdata) {
    const currentDeliveryValue = getDeliveryValue(formdata);
    return ensureCurrentAddressInList(addressList, currentDeliveryValue);
  }

  function getBillingAddressList(formData) {
    const currentBillingValue = getBillingValue(formData);
    return ensureCurrentAddressInList(sellerAddressList, currentBillingValue);
  }

  function getPickupAddressList(formData) {
    const currentPickupValue = getPickupValue(formData);
    return ensureCurrentAddressInList(sellerAddressList, currentPickupValue);
  }

  const handleDeliveryAddressChange = (selectedOption) => {
    if (selectedOption?.id) {
      setAddressToOrder({
        addressId: selectedOption?.id,
        addressType: 'DELIVERY_ADDRESS',
        address: formatAddress(selectedOption),
      });
      setFieldValue('deliveryAddress', selectedOption);
    }
  };

  const handleBillingAddressChange = (selectedOption) => {
    if (selectedOption?.id) {
      setAddressToOrder({
        addressId: selectedOption?.id,
        addressType: 'BILLING_ADDRESS',
      });
      setFieldValue('billingAddress', selectedOption);
    }
  };

  const handlePickupAddressChange = (selectedOption) => {
    if (selectedOption?.id) {
      setAddressToOrder({
        addressId: selectedOption?.id,
        addressType: 'PICKUP_ADDRESS',
        address: formatAddress(selectedOption),
      });
      setFieldValue('pickupAddress', selectedOption);
    }
  };

  useEffect(() => {
    if (sellerAddressList?.length > 0 && !getPickupValue(values)) {
      handlePickupAddressChange(sellerAddressList[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerAddressList]);

  return (
    <CardWithTitleAndButton title="Address Details">
      <Row className="mt-3">
        <Card style={{ padding: 0 }}>
          <CardHeader
            style={{
              padding: '8px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontWeight: '600',
            }}
          >
            <div>Buyer&apos;s Address</div>
            <div>
              <ButtonV2
                style={{ fontWeight: '400' }}
                className="border-radius-4"
                size="sm"
                color="primary"
                key="edit"
                onClick={() => setShowAddressForm(1)}
              >
                {plusIconSvg}
                <span style={{ paddingLeft: '6px' }}>Add</span>
              </ButtonV2>
            </div>
          </CardHeader>
          <CardBody style={{ padding: '8px 12px' }}>
            <Row className="mt-3">
              <Col sm={6}>
                <h5 className="fw-medium" style={{ fontSize: 14 }}>
                  Delivery Address:
                </h5>
                <Field
                  label=""
                  name="deliveryAddress"
                  type="select"
                  component={ReactSelect}
                  placeholder="Select Delivery Address"
                  data={getDeliveryAddressList(values)}
                  onChange={handleDeliveryAddressChange}
                  keys={{ id: 'id', label: 'label' }}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => formatAddress(option)}
                  value={getDeliveryValue(values)}
                  defaultValue={getDeliveryValue(values)}
                />
              </Col>
              <Col sm={6}>
                <h5 className="fw-medium" style={{ fontSize: 14 }}>
                  Billing Address:
                </h5>
                <Field
                  label=""
                  name="billingAddress"
                  type="select"
                  component={ReactSelect}
                  placeholder="Select Billing Address"
                  data={getBillingAddressList(values)}
                  onChange={handleBillingAddressChange}
                  keys={{ id: 'id', label: 'label' }}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => formatAddress(option)}
                  value={getBillingValue(values)}
                  defaultValue={getBillingValue(values)}
                />
              </Col>
              {!!showAddressForm && showAddressForm === 1 && (
                <div style={{ height: '10px', backgroundColor: '#F8F8F8' }} />
              )}
              <Col sm={12} className="mt-3">
                {!!showAddressForm && showAddressForm === 1 && (
                  <AddressForm
                    values={values}
                    show={showAddressForm}
                    onSaveAddress={addNewAddress}
                    setShowAddressForm={setShowAddressForm}
                  />
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
        <Card style={{ padding: 0 }}>
          <CardHeader
            style={{
              padding: '8px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontWeight: '600',
            }}
          >
            <div>Seller&apos;s Address</div>
            <div>
              <ButtonV2
                style={{ fontWeight: '400' }}
                className="border-radius-4"
                size="sm"
                color="primary"
                key="edit"
                onClick={() => setShowAddressForm(2)}
              >
                {plusIconSvg}
                <span style={{ paddingLeft: '6px' }}>Add</span>
              </ButtonV2>
            </div>
          </CardHeader>
          <CardBody style={{ padding: '16px 12px' }}>
            <Row>
              <Col sm={12}>
                <h5 className="fw-medium" style={{ fontSize: 14 }}>
                  Pickup Address:
                </h5>
                <Field
                  label=""
                  name="pickupAddress"
                  type="select"
                  component={ReactSelect}
                  data={getPickupAddressList(values)}
                  onChange={handlePickupAddressChange}
                  keys={{ id: 'id', label: 'label' }}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => formatAddress(option)}
                  value={getPickupValue(values)}
                  defaultValue={getPickupValue(values)}
                />
              </Col>
              {!!showAddressForm && showAddressForm === 2 && (
                <div style={{ height: '10px', backgroundColor: '#F8F8F8' }} />
              )}
              <Col sm={12} className="mt-3">
                {!!showAddressForm && showAddressForm === 2 && (
                  <AddressForm
                    values={values}
                    show={showAddressForm}
                    onSaveAddress={addNewAddress}
                    setShowAddressForm={setShowAddressForm}
                  />
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Row>
    </CardWithTitleAndButton>
  );
};

export default AddressSection;
